import { Component, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { EmailValidator, Broadcaster } from '../../utils/index';
import { Location } from '@angular/common';

import { CustomerSelectorPluginService } from './customerselector-plugin.service';
import { BaseModalPageComponent } from '../../base-modal.component';
import { ModalComponent } from '../../modal/modal.component';
import { ConfirmComponent } from '../../modal/confirm.component';
import * as _ from 'lodash';
import { maxPhoneLengthValidator } from '../../utils/maxPhoneLength.directive';
import { LocalEntityService } from '../../shared/services/index';

@Component({
  selector: 'machineselector-edit',
  templateUrl: './customerselector-edit.component.html',
  standalone: false
})
export class CustomerSelectorEditPageComponent extends BaseModalPageComponent implements OnInit {
  inspectionId: string;
  pluginId: string;
  public customer: any;
  public plugin: any;
  customerForm: UntypedFormGroup;

  @ViewChild('languagesModal', { static: true })
  languageModal: ModalComponent;
  @ViewChild('countriesModal', { static: true })
  countriesModal: ModalComponent;
  @ViewChild('menuModal', { static: true })
  menuModal: ModalComponent;
  @ViewChild('deleteConfirm', { static: true })
  deleteConfirm: ConfirmComponent;
  @ViewChild('localEntitiesModal', { static: true })
  localEntitiesModal: ModalComponent;
  public languages: any;
  public countries: any;
  public localEntities: any;
  public processing = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private service: CustomerSelectorPluginService,
    location: Location,
    private cdRef: ChangeDetectorRef,
    private broadcaster: Broadcaster,
    private localEntityService: LocalEntityService
  ) {
    super(location);
  }

  initRoute(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      const id = params['inspectionId'];
      if (id == null) this.goToBack();

      this.inspectionId = id;

      const pluginId = params['pluginId'];
      if (pluginId == null) this.goToBack();

      this.pluginId = pluginId;

      this.initData();
    });
  }

  save(model: any) {
    this.processing = true;
    this.service.updateCustomer(this.inspectionId, this.pluginId, model).subscribe((el) => {
      this.processing = false;
      this.goToBack();
    });
  }

  cancel() {
    this.goToBack();
  }

  ngOnInit() {
    this.initRoute();
  }

  initData() {
    this.languages = _.orderBy(this.service.getLanguages(), ['label']);

    this.service.getCountries().subscribe((data: any) => {
      this.countries = _.orderBy(data, ['label']);
      this.localEntityService.getLocalEntities().subscribe((localEntities: any) => {
        this.localEntities = _.orderBy(localEntities, ['label']);

        this.service.getPlugin(this.inspectionId, this.pluginId).subscribe((el) => {
          this.plugin = el;
          this.customer = el.inspection.insptectionInspectionCustomer;
          if (this.customer == null || this.customer.length === 0) {
            this.newCutomer();
          }

          this.initForm();
          this.cdRef.detectChanges();
        });
      });
    });
  }

  newCutomer() {
    this.customer = {
      CrmId: '',
      Name: '',
      Address: '',
      City: '',
      Country: '',
      LocalEntity: '',
      Contact: '',
      Tel: '',
      Email: ''
    };
  }

  private openGenericSelector(values: any, modal: any, controlName: string, search: boolean = false) {
    const selectedIndex = this.customerForm.controls[controlName].value;
    this.openSelector(modal, values, selectedIndex, { search });
  }

  public openLanguagesSelector(): void {
    this.openGenericSelector(this.languages, this.languageModal, 'Language');
  }

  public openCountriesSelector(): void {
    this.openGenericSelector(this.countries, this.countriesModal, 'Country', true);
  }

  public openLocalEntitiesSelector(): void {
    this.openGenericSelector(this.localEntities, this.localEntitiesModal, 'LocalEntity', true);
  }

  public selectLanguagesMenu(item: any) {
    this.onSelectMenu(item, 'Language');
  }

  public selectCountriesMenu(item: any) {
    this.onSelectMenu(item, 'Country');
    const leToSelect = _.find(this.localEntities, (le: any) => _.indexOf(le.countries, item.selectedIndex) !== -1);
    if (leToSelect) {
      leToSelect.selectedIndex = leToSelect.value;
      this.selectLocalEntitiesMenu(leToSelect);
    }
  }

  public selectLocalEntitiesMenu(item: any) {
    this.onSelectMenu(item, 'LocalEntity');
  }

  private onSelectMenu(item: any, controlName: string) {
    const selectedIndex = this.customerForm.controls[controlName].value === item.selectedIndex ? '' : item.selectedIndex;
    this.customerForm.controls[controlName].setValue(selectedIndex);
  }

  public onLanguagesMenuClose() {
    this.onSelectMenuClose('Language');
  }

  public onCountriesMenuClose(e: any) {
    this.onSelectMenuClose('Country');
  }

  public onLocalEntitiesMenuClose(e: any) {
    this.onSelectMenuClose('LocalEntity');
  }

  private onSelectMenuClose(controlName: string) {
    this.customerForm.controls[controlName].markAsTouched();
  }

  initForm() {
    this.customerForm = new UntypedFormGroup({
      CrmId: new UntypedFormControl(this.customer.CrmId),
      Name: new UntypedFormControl(this.customer.Name),
      Address: new UntypedFormControl(this.customer.Address),
      City: new UntypedFormControl(this.customer.City),
      Country: new UntypedFormControl(this.customer.Country),
      LocalEntity: new UntypedFormControl(this.customer.LocalEntity),
      Contact: new UntypedFormControl(this.customer.Contact),
      Tel: new UntypedFormControl(this.customer.Tel, [
        Validators.pattern(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/),
        Validators.minLength(10),
        maxPhoneLengthValidator(21)
      ]),
      Email: new UntypedFormControl(this.customer.Email, Validators.compose([EmailValidator.validate])),
      Language: new UntypedFormControl(this.customer.Language)
    });
  }

  public selectMenu(item: any) {
    switch (item) {
      case 1:
        this.openDeleteConfirm();
        break;
      case 10:
        this.gotoEditJsonPage();
        break;
      case 11:
        this.gotoEditPage();
        break;
      default:
    }
  }

  gotoEditJsonPage() {
    return this.router.navigate([this.inspectionsRoute, this.inspectionId, 'points', -1, 'plugins', this.pluginId, 'editjson']);
  }

  gotoEditPage() {
    return this.router.navigate([this.inspectionsRoute, this.inspectionId, 'points', -1, 'plugins', this.pluginId, 'edit']);
  }

  openDeleteConfirm() {
    this.deleteConfirm.open();
  }

  deletePlugin() {
    this.service.deletePlugin(this.inspectionId, null, this.pluginId).subscribe((success: boolean) => {
      if (success) {
        this.goToBack();
      }
    });
  }
}
